import React from 'react'
import ButtonGlobal from '@shared/Button'
import { SubmitHandler, useFormContext, useFormState } from 'react-hook-form'
import { OrderSalaryCardType, OrderType } from 'store/types/salaryCard'
import Dialog from '@shared/Dialog'
import { useState } from 'react'
import {
  useFetchCreateSalaryCardMutation,
  useFetchUpdateOrderStatusMutation,
} from 'store/reducers/salaryCards/salaryCardList.api'
import { AsanSigning } from '@shared/AsanSigning'
import { ModalProvider } from '@shared/Modal'
import ResumeModalContent from 'pages/Orders/SalaryCardOrder/entities/ResumeModalContent/ResumeModalContent'
import { columns } from '../../../../constants/salarycardform.columns'
import { useHistory } from 'react-router-dom'
import { Col, Row, Space, Typography } from 'antd'
import { useFetchBranchListQuery } from 'store/reducers/configs/configs.api'
import { AlertIconIntoDetail } from '@modules/CustomIcons'
import { useTranslation } from 'react-i18next'

export function FieldActionsRight() {
  const [open, setOpen] = useState(false)
  const { handleSubmit, reset, trigger, getValues } = useFormContext()
  const { isValid } = useFormState()

  const navigate = useHistory()
  const { t } = useTranslation()

  const [
    fetchCreateSalaryCard,
    { isSuccess, isLoading, status, data: orderData, reset: resetQuery, error },
  ] = useFetchCreateSalaryCardMutation({ fixedCacheKey: 'createSalaryCard' })

  const [
    updateOrderStatus,
    { status: upStatus, isSuccess: isUpSuccess, reset: resetUp },
  ] = useFetchUpdateOrderStatusMutation()

  const onSubmit: SubmitHandler<OrderSalaryCardType> = (data) => {
    setOpen(true)
    fetchCreateSalaryCard(data)
  }

  const { data: branchList = [] } = useFetchBranchListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })

  const getBranchLabel = (value: string) =>
    branchList.find((item) => item.value === value)?.label || ''

  const resumeCompanyInfo_1 = [
    {
      title: t('orders.salaryCardOrder.inputLabels.cardCurrency'),
      value: getValues('cardCurrency'),
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.cardType'),
      value: getValues('cardType'),
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.orderTypes'),
      value: t(`orders.salaryCardOrder.constants.${getValues('orderType')}`),
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.receivingTransfer'),
      value: t(
        `orders.salaryCardOrder.constants.${getValues('orderDeliveryType')}`
      ),
    },
  ]

  const resumeCompanyInfo_2 = [
    {
      title: t('orders.salaryCardOrder.inputLabels.orderBranch'),
      value: getBranchLabel(getValues('orderBranch')),
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.BranchTransferred'),
      value: getBranchLabel(getValues('branchTransferred')),
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.totalNumberOfRejections'),
      value: `${getValues('tableList').length}/0`,
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.note'),
      value: getValues('note'),
    },
  ]

  const resumeCompanyInfo_3 = [
    {
      title: t('orders.salaryCardOrder.inputLabels.fullNameEtibar'),
      value: getValues('trustedPerson.fullName'),
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.pin'),
      value: getValues('trustedPerson.pin'),
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.phoneNumber'),
      value: getValues('trustedPerson.phone'),
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.expiryDate'),
      value: getValues('trustedPerson.expiryDate'),
    },
  ]
  return (
    <>
      <ButtonGlobal
        customType="default"
        color="#015CD1"
        onClick={() => {
          reset()
        }}
      >
        {t('orders.salaryCardOrder.buttonsLabels.update')}
      </ButtonGlobal>
      <ModalProvider
        title={t('orders.detailsOrder')}
        destroyOnClose
        width="calc(100vw - 20%)"
        modalContent={
          <ResumeModalContent
            columns={columns()}
            tableData={getValues('tableList')}
            dataSource={[
              resumeCompanyInfo_1,
              resumeCompanyInfo_2,
              resumeCompanyInfo_3,
            ]}
          />
        }
        footer={({ closeModal }) => (
          <Row justify="space-between" align="middle">
            <Col span={6}>
              {getValues('orderType') === OrderType.URGENT && (
                <Row
                  style={{
                    width: '350px',
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: '#F3F6FA',
                    fontSize: '10px',
                    fontWeight: 500,
                    display: 'flex',
                  }}
                >
                  <div style={{ marginRight: '8px' }}>
                    <AlertIconIntoDetail />
                  </div>

                  <Typography.Text>
                    {t('orders.salaryCardOrder.constants.commissionFee')}
                  </Typography.Text>
                </Row>
              )}
            </Col>
            <Col>
              <Space>
                <ButtonGlobal
                  customType="default"
                  color="#015CD1"
                  onClick={() => closeModal()}
                >
                  {t('orders.salaryCardOrder.buttonsLabels.close')}
                </ButtonGlobal>
                <ButtonGlobal
                  loading={isLoading}
                  customType="primary"
                  disabled={!isValid}
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('newTransfer.create')}
                </ButtonGlobal>
                <AsanSigning
                  docType="SCO"
                  asanKey="asanSignWithCreate"
                  handleSigning={() => {}}
                  checkSignCallback={() => isValid || isLoading}
                  status={upStatus}
                  message={
                    isUpSuccess
                      ? t('orders.salaryCardOrder.restrictionTexts.asanSuccess')
                      : undefined
                  }
                  onComplete={(data) => {
                    updateOrderStatus({
                      orderNo: orderData.orderNo,
                      ...data,
                    })
                  }}
                  afterCloseFn={() => {
                    reset()
                    resetQuery()
                    resetUp()
                    closeModal()
                    navigate.push('/orders/salary-card')
                  }}
                  beforeFromSigning={async () => {
                    const isValid = await trigger()

                    if (isValid) {
                      let requestData
                      await handleSubmit((data) => {
                        requestData = data
                      })()

                      return {
                        mutation: fetchCreateSalaryCard,
                        requestData,
                        fileNameKey: 'orderNo',
                        successMessage: `${t('orders.createButAsanError')} `,
                      }
                    }
                  }}
                />
              </Space>
              <Dialog
                open={open}
                title={t('common.attention')}
                message={isSuccess ? t('orders.createdSucess') : error?.data}
                modalType={status}
                afterClose={() => {
                  setOpen(false)
                  if (isSuccess) {
                    reset()
                    resetQuery()
                    closeModal()
                    navigate.push('/orders/salary-card')
                  }
                }}
              />
            </Col>
          </Row>
        )}
        bodyStyle={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
        }}
      >
        {({ openModal }) => (
          <ButtonGlobal
            loading={isLoading}
            customType="primary"
            disabled={!isValid}
            onClick={() => {
              openModal('')
            }}
          >
            {t('newTransfer.nextButton')}
          </ButtonGlobal>
        )}
      </ModalProvider>
    </>
  )
}
